import React, { useState, useEffect, useRef } from 'react';
import { useUsers } from './UsersContext';
import { T, inputCls, inputStyle, onFocus, onBlur, Th, Tr, ActionBtn, Modal, UserDetailFields, TableCard, TableBar, SearchInput, Toast, PrimaryBtn, visibilityBadge } from './theme';
import { useRefresh } from './RefreshContext';
import API_BASE_URL from '../config/api';

const AdminEventsTab = () => {
  const { getUserName, getUserObj } = useUsers();
  const { refresh, triggerRefresh } = useRefresh();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [viewEvent, setViewEvent] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [search, setSearch] = useState('');
  const [editEvent, setEditEvent] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', event_start_date: '', event_end_date: '', event_details: '' });
  const [editImageFile, setEditImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const editFileRef = useRef(null);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/vrv-events-getAll`, { method: 'GET' });
      const data = await res.json();
      const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
      setEvents((parsed.data || []).sort((a, b) => new Date(b.event_start_date || 0) - new Date(a.event_start_date || 0)));
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchEvents(); }, [refresh]); // eslint-disable-line

  const showMsg = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3500);
  };

  const toggleSelect = id => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedIds(selectedIds.length === events.length ? [] : events.map(e => e.event_id));

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) { showMsg('Select at least one item', 'error'); return; }
    try {
      const token = localStorage.getItem('vrvToken');
      let allSuccess = true;
      for (const event_id of selectedIds) {
        const res = await fetch(`${API_BASE_URL}/vrv-events-action`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ event_id, action }) });
        if (!res.ok) allSuccess = false;
      }
      showMsg(allSuccess ? `${action} applied to ${selectedIds.length} item(s)` : 'Some actions failed', allSuccess ? 'success' : 'error');
      setSelectedIds([]); fetchEvents(); triggerRefresh();
    } catch { showMsg('Error connecting to server', 'error'); }
  };

  const handleEdit = (event) => {
    setEditEvent(event);
    setEditForm({ title: event.title || '', event_start_date: event.event_start_date || '', event_end_date: event.event_end_date || '', event_details: event.event_details || '' });
    setEditImageFile(null);
  };

  const hasEventChanges = () => editEvent && (
    editForm.title !== (editEvent.title || '') || editForm.event_start_date !== (editEvent.event_start_date || '') ||
    editForm.event_end_date !== (editEvent.event_end_date || '') || editForm.event_details !== (editEvent.event_details || '') || editImageFile !== null
  );

  const handleEditSave = async () => {
    if (!editForm.title || !editForm.event_start_date || !editForm.event_end_date || !editForm.event_details) { showMsg('All fields are required', 'error'); return; }
    if (!hasEventChanges()) { showMsg('No changes to save', 'error'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('vrvToken');
      const res = await fetch(`${API_BASE_URL}/vrv-events-action`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ event_id: editEvent.event_id, action: 'UPDATE', ...editForm }) });
      if (editImageFile && res.ok) {
        const uploadUrlRes = await fetch(`${API_BASE_URL}/vrv-events-generate-upload-url?event_id=${editEvent.event_id}&fileName=${editImageFile.name}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } });
        const uploadUrlData = await uploadUrlRes.json();
        const parsedUpload = typeof uploadUrlData.body === 'string' ? JSON.parse(uploadUrlData.body) : uploadUrlData;
        if (parsedUpload.uploadUrl) {
          await fetch(parsedUpload.uploadUrl, { method: 'PUT', headers: { 'Content-Type': editImageFile.type }, body: editImageFile });
          if (parsedUpload.imageUrl) await fetch(`${API_BASE_URL}/vrv-events-action`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ event_id: editEvent.event_id, action: 'UPDATE_IMAGE', event_image_url: parsedUpload.imageUrl + '?v=' + Date.now() }) });
        }
      }
      if (res.ok) { showMsg('Event updated!', 'success'); setEditEvent(null); fetchEvents(); triggerRefresh(); }
      else showMsg('Update failed', 'error');
    } catch { showMsg('Error connecting to server', 'error'); }
    setSaving(false);
  };

  const filteredEvents = events.filter(e => search === '' || Object.values(e).some(val => String(val).toLowerCase().includes(search.toLowerCase())));

  const Field = ({ label, children, col2 }) => (
    <div className={col2 ? 'md:col-span-2' : ''}>
      <label className="block text-[10px] font-black tracking-widest uppercase mb-1.5" style={{ color: T.muted }}>{label}</label>
      {children}
    </div>
  );

  return (
    <div>
      <div className="mb-4"><SearchInput value={search} onChange={e => setSearch(e.target.value)} /></div>
      {loading && <p className="text-center py-8 text-sm" style={{ color: T.muted }}>Loading...</p>}

      {!loading && events.length > 0 && (
        <>
          {/* Desktop Table */}
          <div className="desktop-table">
            <TableCard>
              <TableBar className="table-bar-wrap">
                <p className="text-xs font-bold" style={{ color: T.muted }}>{selectedIds.length > 0 ? `${selectedIds.length} selected` : `${filteredEvents.length} events`}</p>
                <div className="flex flex-wrap gap-2">
                  {[['Public', 'gold'], ['Private', 'muted'], ['Delete', 'red']].map(([label, color]) => (
                    <ActionBtn key={label} onClick={() => handleBulkAction(label.toUpperCase())} disabled={selectedIds.length === 0} color={color}>{label}</ActionBtn>
                  ))}
                </div>
              </TableBar>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr>
                    <th className="px-3 py-3 w-8" style={{ backgroundColor: T.stripe, borderBottom: `1px solid ${T.border}` }}>
                      <input type="checkbox" checked={selectedIds.length === events.length && events.length > 0} onChange={toggleSelectAll} />
                    </th>
                    <Th>Title</Th><Th>Created By</Th><Th>Start Date</Th><Th>End Date</Th><Th>Visibility</Th><Th center>View</Th><Th center>Edit</Th>
                  </tr></thead>
                  <tbody>
                    {filteredEvents.map((event, i) => (
                      <Tr key={event.event_id || i} selected={selectedIds.includes(event.event_id)}>
                        <td className="px-3 py-2.5"><input type="checkbox" checked={selectedIds.includes(event.event_id)} onChange={() => toggleSelect(event.event_id)} /></td>
                        <td className="px-3 py-2.5 font-medium" style={{ color: T.ink }}>{event.title || '—'}</td>
                        <td className="px-3 py-2.5">{event.createdBy ? <button onClick={() => { const u = getUserObj(event.createdBy); if (u) setViewUser(u); }} className="font-bold hover:underline" style={{ color: T.gold }}>{getUserName(event.createdBy) || '...'}</button> : '—'}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: T.muted }}>{event.event_start_date ? new Date(event.event_start_date).toLocaleDateString() : '—'}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: T.muted }}>{event.event_end_date ? new Date(event.event_end_date).toLocaleDateString() : '—'}</td>
                        <td className="px-3 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest" style={visibilityBadge(event.visibility)}>{event.visibility}</span></td>
                        <td className="px-3 py-2.5 text-center"><button onClick={() => setViewEvent(event)} className="font-black text-[10px] uppercase tracking-widest hover:underline" style={{ color: T.dark }}>View</button></td>
                        <td className="px-3 py-2.5 text-center"><button onClick={() => handleEdit(event)} className="font-black text-[10px] uppercase tracking-widest hover:underline" style={{ color: T.gold }}>Edit</button></td>
                      </Tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TableCard>
          </div>

          {/* Mobile Cards */}
          <div className="mobile-cards">
            <div className="flex flex-wrap gap-2 mb-3">
              {[['Public', 'gold'], ['Private', 'muted'], ['Delete', 'red']].map(([label, color]) => (
                <ActionBtn key={label} onClick={() => handleBulkAction(label.toUpperCase())} disabled={selectedIds.length === 0} color={color}>{label}</ActionBtn>
              ))}
            </div>
            {filteredEvents.map((event, i) => (
              <div key={event.event_id || i} className="vrv-mobile-card" style={{ border: selectedIds.includes(event.event_id) ? `1.5px solid ${T.gold}` : undefined }}>
                <div className="flex items-start gap-3 mb-2">
                  <input type="checkbox" checked={selectedIds.includes(event.event_id)} onChange={() => toggleSelect(event.event_id)} className="mt-1" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate" style={{ color: T.navy }}>{event.title || '—'}</p>
                    <p className="text-xs" style={{ color: T.muted }}>{event.event_start_date ? new Date(event.event_start_date).toLocaleDateString() : '—'} – {event.event_end_date ? new Date(event.event_end_date).toLocaleDateString() : '—'}</p>
                  </div>
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase flex-shrink-0" style={visibilityBadge(event.visibility)}>{event.visibility}</span>
                </div>
                <div className="text-xs mb-3">
                  <span style={{ color: T.muted }}>By: </span>
                  {event.createdBy ? <button onClick={() => { const u = getUserObj(event.createdBy); if (u) setViewUser(u); }} className="font-bold" style={{ color: T.gold }}>{getUserName(event.createdBy) || '...'}</button> : '—'}
                </div>
                <div className="flex gap-2 pt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                  <ActionBtn onClick={() => setViewEvent(event)} color="muted" small>View</ActionBtn>
                  <ActionBtn onClick={() => handleEdit(event)} color="gold" small>Edit</ActionBtn>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {!loading && events.length === 0 && <p className="text-center py-8 text-sm" style={{ color: T.muted }}>No events found.</p>}

      {viewEvent && (
        <Modal onClose={() => setViewEvent(null)} title={viewEvent.title} subtitle="Event Preview" wide>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {[['Start Date', viewEvent.event_start_date ? new Date(viewEvent.event_start_date).toLocaleString() : '—'], ['End Date', viewEvent.event_end_date ? new Date(viewEvent.event_end_date).toLocaleString() : '—']].map(([label, val]) => (
              <div key={label}>
                <p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: T.muted }}>{label}</p>
                <p className="text-sm px-3 py-2 rounded-lg" style={{ backgroundColor: T.stripe, color: T.ink, border: `1px solid ${T.border}` }}>{val}</p>
              </div>
            ))}
          </div>
          {viewEvent.event_image_url && <img src={viewEvent.event_image_url} alt={viewEvent.title} className="w-full max-h-[300px] object-contain rounded-xl mb-4" style={{ border: `1px solid ${T.border}` }} />}
          {viewEvent.event_details && <p className="text-sm leading-relaxed whitespace-pre-wrap mb-6" style={{ color: T.muted, borderTop: `1px solid ${T.border}`, paddingTop: '1rem' }}>{viewEvent.event_details}</p>}
          <PrimaryBtn onClick={() => setViewEvent(null)}>Close</PrimaryBtn>
        </Modal>
      )}

      {editEvent && (
        <Modal onClose={() => setEditEvent(null)} title="Edit Event" subtitle="Event Management" wide>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Title *" col2><input type="text" value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} className={inputCls} style={inputStyle} onFocus={onFocus} onBlur={onBlur} /></Field>
            <Field label="Start Date *"><input type="datetime-local" value={editForm.event_start_date} onChange={e => setEditForm({ ...editForm, event_start_date: e.target.value })} min={new Date().toISOString().slice(0, 10) + 'T00:00'} className={inputCls} style={inputStyle} onFocus={onFocus} onBlur={onBlur} /></Field>
            <Field label="End Date *"><input type="datetime-local" value={editForm.event_end_date} onChange={e => setEditForm({ ...editForm, event_end_date: e.target.value })} className={inputCls} style={inputStyle} onFocus={onFocus} onBlur={onBlur} /></Field>
            <Field label="Replace Image" col2>
              {editEvent.event_image_url && !editImageFile && <img src={editEvent.event_image_url} alt="Current" className="h-20 object-contain rounded-xl mb-2" style={{ border: `1px solid ${T.border}` }} />}
              {editImageFile && <img src={URL.createObjectURL(editImageFile)} alt="New" className="h-20 object-contain rounded-xl mb-2" style={{ border: `1px solid ${T.border}` }} />}
              <input ref={editFileRef} type="file" accept="image/*" onChange={e => setEditImageFile(e.target.files[0])} className={inputCls} style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
            </Field>
            <Field label="Event Details *" col2><textarea value={editForm.event_details} onChange={e => setEditForm({ ...editForm, event_details: e.target.value })} rows="4" className={inputCls + ' resize-none'} style={inputStyle} onFocus={onFocus} onBlur={onBlur} /></Field>
          </div>
          <div className="flex gap-3 mt-6">
            <ActionBtn onClick={() => setEditEvent(null)} color="muted">Cancel</ActionBtn>
            <button onClick={handleEditSave} disabled={saving || !hasEventChanges()}
              className="font-black px-6 py-2.5 rounded-lg text-xs tracking-widest uppercase transition-opacity disabled:opacity-40"
              style={{ backgroundColor: T.dark, color: '#FAF9F6' }}>
              {saving ? 'Saving...' : 'Update Event →'}
            </button>
          </div>
        </Modal>
      )}

      {viewUser && <Modal onClose={() => setViewUser(null)} title="User Details" subtitle="Member Info" wide><UserDetailFields user={viewUser} /><div className="mt-6"><PrimaryBtn onClick={() => setViewUser(null)}>Close</PrimaryBtn></div></Modal>}
      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </div>
  );
};

export default AdminEventsTab;
